"use client"

import { useState, useEffect, useCallback } from "react"
import DataTable from "react-data-table-component"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Input,
  FormGroup,
  Label,
} from "reactstrap"
import {
  FaPrint,
  FaUsers,
  FaIdBadge,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaHistory,
  FaArrowRight,
  FaSearch,
  FaUser,
  FaImage,
  FaBell,
  FaLayerGroup,
} from "react-icons/fa"
import { toast } from "react-toastify"
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs"
import classnames from "classnames"
import PrintingStaffListUpdated from "./printing-staff-list"
import BackBadgePrintModal from "./back-badge-print-modal"
import ConsolidatedRemainingBadges from "./ConsolidatedRemainingBadges"
import { printOrderService, type PrintOrder } from "../../../Service/print-order-service"

const PrintingContainerUpdated = () => {
  const [activeTab, setActiveTab] = useState<"pending" | "completed" | "consolidated">("pending")
  const [currentStep, setCurrentStep] = useState<"loading" | "list" | "staff">("loading")
  const [nonPrintedOrders, setNonPrintedOrders] = useState<PrintOrder[]>([])
  const [printedOrders, setPrintedOrders] = useState<PrintOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<PrintOrder | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isBackBadgeModalOpen, setIsBackBadgeModalOpen] = useState(false)

  // Notification state — keyed by order ID so each row tracks independently
  const [notificationLoadingMap, setNotificationLoadingMap] = useState<Record<number, boolean>>({})
  const [smsSentMap, setSmsSentMap] = useState<Record<number, boolean>>({})

  // Search states
  const [pendingSearchTerm, setPendingSearchTerm] = useState("")
  const [completedSearchTerm, setCompletedSearchTerm] = useState("")
  const [pendingCount, setPendingCount] = useState(0)
  const [totalWithoutBadges, setTotalWithoutBadges] = useState(0)
  const [totalWithBadges, setTotalWithBadges] = useState(0)
  const [completedCount, setCompletedCount] = useState(0)

  // Fetch orders data with search
  const fetchOrders = useCallback(async () => {
    setLoading(true)
    try {
      const [nonPrintedResponse, printedResponse] = await Promise.all([
        printOrderService.getNonPrintedOrders({
          search: pendingSearchTerm,
          page_size: "all",
        }),
        printOrderService.getPrintedOrders({
          search: completedSearchTerm,
          page_size: "all",
        }),
      ])

      setNonPrintedOrders(nonPrintedResponse.orders || [])
      setPrintedOrders(printedResponse.orders || [])
      setPendingCount(nonPrintedResponse.count || 0)
      setCompletedCount(printedResponse.count || 0)

      // setTotalWithoutBadges(nonPrintedResponse.total_without_badge || 0)
      // setTotalWithBadges(printedResponse.total_with_badges || 0)
      setCurrentStep("list")
      setError(null)
    } catch (err) {
      console.error("Error fetching orders:", err)
      setError("Failed to load print orders")
      setCurrentStep("list")
    } finally {
      setLoading(false)
    }
  }, [pendingSearchTerm, completedSearchTerm])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const handleSelectOrder = (order: PrintOrder) => {
    console.log("Selected order:", order)
    setSelectedOrder(order)
    setCurrentStep("staff")
    toast.success(`Loading staff for order ${order.order_number}`)
  }

  const handleBackToList = () => {
    setSelectedOrder(null)
    setCurrentStep("list")
  }

  const handlePrintingComplete = () => {
    toast.success("Badge printing completed!")
    fetchOrders() // Refresh the orders
    handleBackToList()
  }

  const handleSendNotification = async (order: PrintOrder) => {
    setNotificationLoadingMap((prev) => ({ ...prev, [order.id]: true }))
    try {
      const result = await printOrderService.sendPrintOrderNotification(order.id)
      if (result.success) {
        setSmsSentMap((prev) => ({ ...prev, [order.id]: true }))
        toast.success(
          result.already_notified
            ? "Focal person was already notified"
            : "Notification sent to focal person"
        )
      } else {
        toast.error(result.error || "Failed to send notification")
      }
    } catch (error: any) {
      toast.error(error?.error || error?.message || "An error occurred while sending notification")
    } finally {
      setNotificationLoadingMap((prev) => ({ ...prev, [order.id]: false }))
    }
  }

  const toggleTab = (tab: "pending" | "completed" | "consolidated") => {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success"
      case "printed":
        return "primary"
      case "pending":
        return "warning"
      case "rejected":
        return "danger"
      default:
        return "secondary"
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // DataTable columns for pending orders
  const pendingOrdersColumns = [
    {
      name: "Order #",
      selector: (row: PrintOrder) => row.order_number,
      cell: (row: PrintOrder) => <code className="bg-light px-2 py-1 rounded">{row.order_number}</code>,
      sortable: true,
      width: "120px",
    },
    {
      name: "Company",
      selector: (row: PrintOrder) => row.participation_details.exhibitor_name,
      cell: (row: PrintOrder) => (
        <div>
          <strong>{row.participation_details.exhibitor_name}</strong>
          <br />
          <small className="text-muted">{row.participation_details.stand_category_name}</small>
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Exhibition",
      selector: (row: PrintOrder) => row.participation_details.exhibition_title,
      cell: (row: PrintOrder) => <div className="small">{row.participation_details.exhibition_title}</div>,
      sortable: true,
      wrap: true,
    },
    {
      name: "Staff Count",
      selector: (row: PrintOrder) => row.order_details.length,
      cell: (row: PrintOrder) => (
        <div className="text-center">
          <Badge color="info" pill>
            <FaUsers className="me-1" />
            {row.order_details.length}
          </Badge>
        </div>
      ),
      sortable: true,
      center: true,
      width: "120px",
    },
    {
      name: "Status",
      selector: (row: PrintOrder) => row.status,
      cell: (row: PrintOrder) => (
        <Badge color={getStatusBadgeColor(row.status)} pill>
          {row.status.toUpperCase()}
        </Badge>
      ),
      sortable: true,
      center: true,
      width: "120px",
    },
    {
      name: "Created",
      selector: (row: PrintOrder) => row.created_at,
      cell: (row: PrintOrder) => <small>{formatDate(row.created_at)}</small>,
      sortable: true,
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row: PrintOrder) => (
        <Button color="primary" size="sm" onClick={() => handleSelectOrder(row)} className="d-flex align-items-center">
          <FaArrowRight className="me-1" size={12} />
          Start Printing
        </Button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ]

  // DataTable columns for completed orders
  const completedOrdersColumns = [
    {
      name: "Order #",
      selector: (row: PrintOrder) => row.order_number,
      cell: (row: PrintOrder) => <code className="bg-light px-2 py-1 rounded">{row.order_number}</code>,
      sortable: true,
      width: "120px",
    },
    {
      name: "Company",
      selector: (row: PrintOrder) => row.participation_details.exhibitor_name,
      cell: (row: PrintOrder) => (
        <div>
          <strong>{row.participation_details.exhibitor_name}</strong>
          <br />
          <small className="text-muted">{row.participation_details.stand_category_name}</small>
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Exhibition",
      selector: (row: PrintOrder) => row.participation_details.exhibition_title,
      cell: (row: PrintOrder) => <div className="small">{row.participation_details.exhibition_title}</div>,
      sortable: true,
      wrap: true,
    },
    {
      name: "Staff Count",
      selector: (row: PrintOrder) => row.order_details.length,
      cell: (row: PrintOrder) => (
        <div className="text-center">
          <Badge color="success" pill>
            <FaIdBadge className="me-1" />
            {row.order_details.length}
          </Badge>
        </div>
      ),
      sortable: true,
      center: true,
      width: "120px",
    },
    {
      name: "Status",
      selector: (row: PrintOrder) => row.status,
      cell: (row: PrintOrder) => (
        <Badge color={getStatusBadgeColor(row.status)} pill>
          <FaCheckCircle className="me-1" />
          {row.status.toUpperCase()}
        </Badge>
      ),
      sortable: true,
      center: true,
      width: "120px",
    },
    {
      name: "Completed",
      selector: (row: PrintOrder) => row.updated_at,
      cell: (row: PrintOrder) => <small>{formatDate(row.updated_at)}</small>,
      sortable: true,
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row: PrintOrder) => {
        const isLoading = notificationLoadingMap[row.id] ?? false
        const isSent = smsSentMap[row.id] ?? row.sms_sent ?? false
        return (
          <div className="d-flex gap-1">
            <Button
              color="info"
              size="sm"
              onClick={() => handleSelectOrder(row)}
              className="d-flex align-items-center"
            >
              <FaEye className="me-1" size={12} />
              View
            </Button>
            <Button
              color={isSent ? "secondary" : "success"}
              size="sm"
              disabled={isLoading || isSent}
              onClick={() => handleSendNotification(row)}
              className="d-flex align-items-center"
              title={isSent ? "Focal person already notified" : "Send SMS notification to focal person"}
            >
              <FaBell className="me-1" size={12} />
              {isLoading ? "Sending…" : isSent ? "Notified" : "Notify"}
            </Button>
          </div>
        )
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "200px",
    },
  ]

  // Custom styles for DataTable
  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #dee2e6",
      },
    },
    headCells: {
      style: {
        fontWeight: "600" as const,
        fontSize: "14px",
        color: "#495057",
      },
    },
    rows: {
      style: {
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
      stripedStyle: {
        backgroundColor: "#f8f9fa",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #dee2e6",
      },
    },
  }

  // Loading state
  if (currentStep === "loading") {
    return (
      <>
        <Breadcrumbs mainTitle="Badge Printing" parent="Badge Printing" />
        <Container fluid>
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <div className="text-center py-5">
                    <Spinner color="primary" size="lg" />
                    <p className="mt-3">Loading print orders...</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  // Staff list state
  if (currentStep === "staff" && selectedOrder) {
    return (
      <>
        <Breadcrumbs mainTitle="Badge Printing" parent="Badge Printing" />
        <Container fluid>
          <Row>
            <Col md="12">
              <PrintingStaffListUpdated
                order={selectedOrder}
                onBackToSelection={handleBackToList}
                onPrintingComplete={handlePrintingComplete}
              />
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  // List state with tabs
  return (
    <>
      <Breadcrumbs mainTitle="Badge Printing" parent="Badge Printing" />
      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="border-0 shadow-sm">
              <CardHeader className="bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-0 text-white">
                      <FaPrint className="me-2" />
                      Badge Printing Center - Print Orders
                    </h4>
                    <small className="opacity-75">Manage badge printing through approved orders</small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button color="warning" size="sm" onClick={() => setIsBackBadgeModalOpen(true)}>
                      <FaImage className="me-1" />
                      Print Badge Back
                    </Button>
                    <Button color="light" size="sm" onClick={fetchOrders}>
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="p-3">
                {error && (
                  <Alert color="danger" className="m-4">
                    {error}
                  </Alert>
                )}

                {/* Navigation Tabs */}
                <Nav tabs className="border-bottom">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "pending" })}
                      onClick={() => toggleTab("pending")}
                      style={{ cursor: "pointer" }}
                    >
                      <FaClock className="me-2" />
                      Pending Printing
                      <Badge color="success" className="ms-2">
                        {pendingCount}
                      </Badge>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "completed" })}
                      onClick={() => toggleTab("completed")}
                      style={{ cursor: "pointer" }}
                    >
                      <FaCheckCircle className="me-2" />
                      Completed Orders
                      <Badge color="primary" className="ms-2">
                        {completedCount}
                      </Badge>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "consolidated" })}
                      onClick={() => toggleTab("consolidated")}
                      style={{ cursor: "pointer" }}
                    >
                      <FaLayerGroup className="me-2" />
                      Consolidated Remaining Badges
                    </NavLink>
                  </NavItem>
                </Nav>

                {/* Tab Content */}
                <TabContent activeTab={activeTab}>
                  {/* Pending Orders Tab */}
                  <TabPane tabId="pending">
                    <div className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">
                          <FaClock className="me-2 text-warning" />
                          Orders Ready for Printing
                        </h5>
                        <Badge color="success" pill className="fs-6">
                          {pendingCount} orders
                        </Badge>
                      </div>

                      {/* Search for Pending Orders */}
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label>
                              <FaSearch className="me-2" />
                              Search Pending Orders
                            </Label>
                            <div className="position-relative">
                              <Input
                                type="text"
                                placeholder="Search by order number, company, exhibition, or staff name..."
                                value={pendingSearchTerm}
                                onChange={(e) => setPendingSearchTerm(e.target.value)}
                                className="ps-5"
                              />
                              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            </div>
                            <small className="text-muted">
                              <FaUser className="me-1" size={12} />
                              You can search by staff member's first name or last name
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>

                      {/* Search Info for Pending */}
                      {pendingSearchTerm && (
                        <div className="mb-3">
                          <Badge color="info" className="me-2">
                            <FaSearch className="me-1" size={12} />
                            Searching: "{pendingSearchTerm}"
                          </Badge>
                          <small className="text-muted">
                            Found {nonPrintedOrders.length} pending orders matching your search
                          </small>
                        </div>
                      )}

                      {nonPrintedOrders.length > 0 ? (
                        <DataTable
                          columns={pendingOrdersColumns}
                          data={nonPrintedOrders}
                          pagination
                          paginationPerPage={10}
                          paginationRowsPerPageOptions={[5, 10, 15, 20, 50]}
                          responsive
                          striped
                          highlightOnHover
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="text-center py-5">
                              <FaClock size={48} className="text-muted mb-3" />
                              <h6 className="text-muted">No Orders Ready for Printing</h6>
                              <p className="text-muted">
                                {pendingSearchTerm
                                  ? "No pending orders match your search criteria"
                                  : "All approved orders have been processed or there are no approved orders yet."}
                              </p>
                            </div>
                          }
                        />
                      ) : (
                        <div className="text-center py-5">
                          <FaClock size={48} className="text-muted mb-3" />
                          <h6 className="text-muted">No Orders Ready for Printing</h6>
                          <p className="text-muted">
                            {pendingSearchTerm
                              ? "No pending orders match your search criteria"
                              : "All approved orders have been processed or there are no approved orders yet."}
                          </p>
                          <Button color="primary" onClick={fetchOrders}>
                            Refresh Orders
                          </Button>
                        </div>
                      )}
                    </div>
                  </TabPane>

                  {/* Completed Orders Tab */}
                  <TabPane tabId="completed">
                    <div className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">
                          <FaCheckCircle className="me-2 text-success" />
                          Completed Print Orders
                        </h5>
                        <Badge color="primary" pill className="fs-6">
                          {completedCount} completed
                        </Badge>
                      </div>

                      {/* Search for Completed Orders */}
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label>
                              <FaSearch className="me-2" />
                              Search Completed Orders
                            </Label>
                            <div className="position-relative">
                              <Input
                                type="text"
                                placeholder="Search by order number, company, exhibition, or staff name..."
                                value={completedSearchTerm}
                                onChange={(e) => setCompletedSearchTerm(e.target.value)}
                                className="ps-5"
                              />
                              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                            </div>
                            <small className="text-muted">
                              <FaUser className="me-1" size={12} />
                              You can search by staff member's first name or last name
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>

                      {/* Search Info for Completed */}
                      {completedSearchTerm && (
                        <div className="mb-3">
                          <Badge color="info" className="me-2">
                            <FaSearch className="me-1" size={12} />
                            Searching: "{completedSearchTerm}"
                          </Badge>
                          <small className="text-muted">
                            Found {printedOrders.length} completed orders matching your search
                          </small>
                        </div>
                      )}

                      {printedOrders.length > 0 ? (
                        <DataTable
                          columns={completedOrdersColumns}
                          data={printedOrders}
                          pagination
                          paginationPerPage={10}
                          paginationRowsPerPageOptions={[5, 10, 15, 20, 50]}
                          responsive
                          striped
                          highlightOnHover
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="text-center py-5">
                              <FaHistory size={48} className="text-muted mb-3" />
                              <h6 className="text-muted">No Completed Orders</h6>
                              <p className="text-muted">
                                {completedSearchTerm
                                  ? "No completed orders match your search criteria"
                                  : "Completed print orders will appear here once badge printing is finished."}
                              </p>
                            </div>
                          }
                        />
                      ) : (
                        <div className="text-center py-5">
                          <FaHistory size={48} className="text-muted mb-3" />
                          <h6 className="text-muted">No Completed Orders</h6>
                          <p className="text-muted">
                            {completedSearchTerm
                              ? "No completed orders match your search criteria"
                              : "Completed print orders will appear here once badge printing is finished."}
                          </p>
                        </div>
                      )}
                    </div>
                  </TabPane>

                  {/* Consolidated Remaining Badges Tab */}
                  <TabPane tabId="consolidated">
                    <div className="p-4">
                      <ConsolidatedRemainingBadges />
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <BackBadgePrintModal isOpen={isBackBadgeModalOpen} onToggle={() => setIsBackBadgeModalOpen((v) => !v)} />
    </>
  )
}

export default PrintingContainerUpdated